import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDetectedAnomalies, useResolveAnomaly } from "@/hooks/useAnomalyDetection";
import { AlertTriangle, TrendingUp, DollarSign, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export function AnomalyDetectionDashboard({ workspaceId }: { workspaceId: string }) {
  const { data: anomalies, isLoading } = useDetectedAnomalies(workspaceId);
  const resolveAnomaly = useResolveAnomaly();

  const handleResolve = async (anomalyId: string) => {
    await resolveAnomaly.mutateAsync({
      anomalyId,
      resolutionNotes: "Reviewed and accepted as valid",
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical": return "border-red-500 text-red-500 bg-red-500/10";
      case "high": return "border-orange-500 text-orange-500 bg-orange-500/10";
      case "medium": return "border-yellow-500 text-yellow-500 bg-yellow-500/10";
      default: return "border-blue-500 text-blue-500 bg-blue-500/10";
    }
  };

  const getAnomalyIcon = (type: string) => {
    switch (type) {
      case "amount_spike": return <DollarSign className="h-4 w-4" />;
      case "usage_spike": return <TrendingUp className="h-4 w-4" />;
      case "frequency": return <Calendar className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return <div>Loading anomalies...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Detected Anomalies</h3>
        <Badge variant="outline">
          {anomalies?.length || 0} active
        </Badge>
      </div>

      <div className="grid gap-4">
        {anomalies?.map((anomaly) => (
          <Card key={anomaly.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    {getAnomalyIcon(anomaly.anomaly_type)}
                    <h4 className="font-medium">
                      {anomaly.anomaly_type.replace(/_/g, " ").toUpperCase()}
                    </h4>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    {anomaly.explanation}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <Badge className={getSeverityColor(anomaly.severity)}>
                      {anomaly.severity}
                    </Badge>
                    <Badge variant="outline">
                      Deviation: {anomaly.deviation_percentage.toFixed(1)}%
                    </Badge>
                    {anomaly.detected_at && (
                      <Badge variant="outline">
                        {formatDistanceToNow(new Date(anomaly.detected_at), { addSuffix: true })}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleResolve(anomaly.id)}
                    disabled={resolveAnomaly.isPending}
                  >
                    Resolve
                  </Button>
                  <Button size="sm">Investigate</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!anomalies || anomalies.length === 0) && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No anomalies detected
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
